import React, { useEffect, useState, useContext } from 'react';
import GroupForm from '../GroupForm';
import AccessRightAccordion from './AccessRightAccordion';
import * as Accordion from '@radix-ui/react-accordion';
import EditGroupUsersInGroup from './EditGroupUsersInGroup';
import SaveCancelButton from './SaveCancelButton';
import EditGroupFolderRights from './EditGroupFolderRights';
import API from '../../../constants/api';
import { AccordionTrigger, AccordionContent } from 'components/common/Accordion'
import { resToJsonHandleErrors } from '../../../helpers/actionHelpers';
import { useDispatch } from 'react-redux';
import { actionSuccess, actionError } from '../../../actions/viewActions';
import LocaleContext from '../../../constants/localization';

const EditGroup = ({ handleCancel, group }) => {
  const [accessRights, setAccessRights] = useState({});
  const [usersInGroup, setUsersInGroup] = useState({});
  const [groupRightsToFolders, setGroupRightsToFolders] = useState([]);
  const [checked, setChecked] = useState([]);

  const [name, setName] = useState(group.name);
  const [email, setEmail] = useState(group.email);
  const [responsable, setResponsable] = useState(group.responsable);
  const [description, setDescription] = useState(group.description);
  const [accessToClassifiedInfo, setAccessToClassifiedInfo] = useState(
    group.accessToClassifiedInfo
  );
  const [createdFromSSO, setCreatedFromSSO] = useState(group.createdFromSSO);

  const [submitDisabled, setSubmitDisabled] = useState(name?.length === 0);
  const [confirmOnCancel, setConfirmOnCancel] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const LS = useContext(LocaleContext);

  useEffect(() => {
    refreshGroup();
  }, []);

  useEffect(() => {
    name?.length === 0 ? setSubmitDisabled(true) : setSubmitDisabled(false);
  }, [name]);

  useEffect(() => {
    if(dataFetched) setConfirmOnCancel(true);
  }, [name, email, responsable, description, accessToClassifiedInfo]);

  const dispatch = useDispatch();

  const handleCancelConfirm = (e) => {
    if (confirmOnCancel && !confirm(LS.manageGroups.confirmCancel)) return;
    handleCancel();
  };
  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new URLSearchParams();
    formData.append('id', group.id);
    if (!createdFromSSO) formData.append('name', name ? name : '');
    formData.append('email', email ? email : '');
    formData.append('responsable', responsable ? responsable : '');
    formData.append('description', description ? description : '');
    formData.append(
      'accessToClassifiedInfo',
      accessToClassifiedInfo ? accessToClassifiedInfo : false
    );

    checked.forEach((e) => formData.append(e, true));

    await fetch(`${API.DAM.MANAGE_GROUPS}?action=saveGroupReact`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    });
    dispatch(actionSuccess('groupSaved'));
    handleCancel();
  };

  const refreshGroup = () => {
    const formData = new URLSearchParams();
    formData.append('id', group.id);
    fetch(`${API.DAM.MANAGE_GROUPS}?action=showGroupReact`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then((res) => resToJsonHandleErrors(res))
      .then((r) => {
        setAccessRights(r.accessRights);
        setUsersInGroup(r.users);
        setGroupRightsToFolders(r.groupRightsToFolders);
        setName(r.group.name);
        setEmail(r.group.email);
        setDescription(r.group.description);
        setResponsable(r.group.responsable);
        setCreatedFromSSO(r.group.createdFromSSO);
        setAccessToClassifiedInfo(r.group.accessToClassifiedInfo);

        const initialChecked = [...checked];
        Object.values(r.accessRights)
          .flat()
          .forEach((e) => {
            if (e.canRead) {
              initialChecked.push(`read_${e.uri}`);
            }
            if (e.canWrite) {
              initialChecked.push(`write_${e.uri}`);
            }
          });
        setChecked(initialChecked);

        setDataFetched(true);
      })
      .catch((e) => dispatch(actionError(e)));
  };

  return (
    <form onSubmit={handleSave}>
      <h2>{LS.manageGroups.groupData}</h2>
      <SaveCancelButton
        disabled={submitDisabled}
        handleCancel={handleCancelConfirm}
        handleSave={handleSave}
      />
      <GroupForm
        edit={true}
        name={name}
        setName={setName}
        email={email}
        setEmail={setEmail}
        responsable={responsable}
        setResponsable={setResponsable}
        description={description}
        setDescription={setDescription}
        accessToClassifiedInfo={accessToClassifiedInfo}
        setAccessToClassifiedInfo={setAccessToClassifiedInfo}
        createdFromSSO={createdFromSSO}
      />
      <Accordion.Root type="multiple" collapsible="true">
        <Accordion.Item value="users">
          <AccordionTrigger>
            <div>
              <i className="fa fa-user" />
              {LS.manageGroups.groupUsers}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <EditGroupUsersInGroup usersInGroup={usersInGroup} />
          </AccordionContent>
        </Accordion.Item>
        <Accordion.Item value="rightsToFolders">
          <AccordionTrigger>
            <div>
              <i className="fa fa-folder" />
              {LS.manageGroups.groupFolders}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <EditGroupFolderRights
              groupRightsToFolders={groupRightsToFolders}
            />
          </AccordionContent>
        </Accordion.Item>
      </Accordion.Root>
      <h2>{LS.manageGroups.accessRights}</h2>
      <AccessRightAccordion
        accessRights={accessRights}
        checked={checked}
        setChecked={setChecked}
      ></AccessRightAccordion>
      <SaveCancelButton
        disabled={submitDisabled}
        handleCancel={handleCancelConfirm}
        handleSave={handleSave}
      />
    </form>
  );
};

export default EditGroup;
